import {
  CategorySlug,
  ExerciseCategory,
  FirestoreDataType,
  FirestoreObject,
  LearningLibraryObject,
  MeditationCategory,
  StudioWorkoutObject,
} from "@sworkit/api";
import Polyglot from "node-polyglot";
// "num_cars": "%{smart_count} car |||| %{smart_count} cars",
import { swApi } from "./app";
import { format, formatDistanceToNow, Locale } from "date-fns";
import {
  enCA,
  es,
  de,
  fr,
  it,
  ja,
  pt,
  ru,
  zhCN,
  hi,
  ko,
  tr,
  hu,
} from "date-fns/locale";
import {
  LanguageString,
  ActivityKey,
  CareArea,
  TranslationObject,
  EquipmentType,
  ExerciseObject,
  ExerciseStance,
  FitnessGoalType,
  WorkoutObject,
  WorkoutLogType,
  GoalType,
  InjuryType,
} from "@sworkit/api";
import th from "date-fns/locale/th";

const polyglot = new Polyglot({
  allowMissing: true,
  onMissingKey: (key) => {
    console.error("translation missing:", key);
    return key;
  },
});

export const locales: LanguageString[] = [
  "en",
  "de",
  "es",
  "esla",
  "fr",
  "it",
  "pt",
  "ru",
  "tr",
  "hi",
  "ja",
  "zh",
  "ko",
  "hu",
  "th",
];

export async function initPolyglot() {
  const languages: { [lang in LanguageString]: string } = {
    de: "Deutsch",
    en: "English",
    es: "Español (España)",
    esla: "Español (América Latina)",
    fr: "Français",
    it: "Italiano",
    pt: "Português",
    ru: "Русский",
    tr: "Türkçe",
    hi: "हिंदी",
    ja: "日本語",
    zh: "简体中文",
    ko: "한국어 [韓國語]",
    hu: "Magyar",
    th: "ไทย",
  };
  polyglot.extend(languages);
  const uiTranslations = swApi.uiTranslations?.data.reduce(
    (acc, translationObject) => {
      const { id, translation } = translationObject;
      acc[id] = translation;
      return acc;
    },
    {},
  );
  // @todo: newTranslations should only be a fallback for translations that may not be published in ui translation data yet
  //cross check these with translations in data. remove any "new" translations that already have all their translations done in
  //swApi.ui_translations (open up non-english to see if the keys have been translated )
  const newTranslations: Partial<{ [key in TranslateKey]: string }> = {
    // PROGRESS: "Progress",
  };
  polyglot.extend(newTranslations);
  console.log("health connect", uiTranslations["HEALTH_CONNECT"]);
  polyglot.extend(uiTranslations);
  const weekTranslations = generateWeekObject(25);
  polyglot.extend(weekTranslations);
  return polyglot;
}

const generateWeekObject = (numWeeks: number) => {
  const result = {};
  for (let i = 1; i <= numWeeks; i++) {
    result[`WEEK_${i}`] = `${translate("WEEK", 1)} ${i}`;
  }
  return result;
};

export function translate(
  key:
    | TranslateKey
    | ActivityKey
    | LanguageString
    | CareArea
    | InjuryType
    | FitnessGoalType
    | LearningLibraryObject["subSection"],
  params?: any,
) {
  // if(swApi && swApi.uiTranslations && !swApi.uiTranslations.get(key)) throw Error(`translation not found: ${key}`)

  if (!key) return "";
  return polyglot.t(key, params);
}
export const englishLanguageKeyMap: {
  [languageString in LanguageString]: string;
} = {
  en: "English",
  de: "German",
  es: "Spanish (Spain)",
  esla: "Spanish (America)",
  fr: "French",
  hi: "Hindi",
  it: "Italian",
  ja: "Japanese",
  ko: "Korean",
  pt: "Portuguese",
  ru: "Russian",
  tr: "Turkish",
  zh: "Chinese",
  hu: "Hungarian",
  th: "Thai",
};
export function translateLanguageStringInEnglish(key: LanguageString) {
  return englishLanguageKeyMap[key];
}

export function formatDate(
  date: Date | number,
  formatPattern: string,
  options?: {
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    locale?: Locale;
    awareOfUnicodeTokens?: boolean;
  },
) {
  if (!date) return null;
  const opt = {
    ...options,
    locale: getDateFnsLocale(swApi?.language),
  };
  try {
    return format(date, formatPattern, opt);
  } catch (err) {
    console.debug("error formatting date: ", date, "err: ", err);
    return null;
  }
}
export function formatDistanceFromToday(date: Date | number) {
  if (!date) return null;
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: getDateFnsLocale(swApi?.language),
  });
}

export function getDateFnsLocale(locale?: LanguageString) {
  const localeKeys: { [key in LanguageString]: Locale } = {
    en: enCA,
    de,
    es,
    esla: es,
    fr,
    hi,
    it,
    ja,
    ko,
    pt,
    ru,
    tr,
    zh: zhCN,
    hu,
    th,
  };
  if (localeKeys[locale]) {
    return localeKeys[locale];
  }
  return localeKeys.en;
}

export function getEmptyLanguageKeys() {
  const emptyLanguageKeys: TranslationObject = {
    en: "",
    de: "",
    es: "",
    esla: "",
    fr: "",
    it: "",
    pt: "",
    ru: "",
    tr: "",
    hi: "",
    ja: "",
    zh: "",
    ko: "",
    hu: "",
    th: "",
  };
  return emptyLanguageKeys;
}

export function getEquipmentName(equipment: EquipmentType) {
  const equipmentTranslations: { [key in EquipmentType]: string } = {
    kettlebell: translate("KETTLEBELL"),
    bodyweight: translate("BODYWEIGHT"),
    dumbbell: translate("DUMBBELL"),
    loop: translate("LOOP"),
    band: translate("BAND"),
    foamroller: translate("FOAMROLLER"),
    barre: translate("BARRE_CHAIR"),
    chair: translate("CHAIR"),
    pilatesball: translate("PILATESBALL"),
  };
  return equipmentTranslations[equipment] || null;
}

export function getImpactTranslation(exercise: ExerciseObject) {
  if (!exercise) return null;
  return exercise.isHighImpact
    ? translate("HIGH")
    : exercise.isLowImpact
      ? translate("LOW")
      : translate("NORMAL");
}

export function getStanceTranslation(stance: ExerciseStance) {
  const stanceTranslations: { [key in ExerciseStance]: string } = {
    standing: translate("STANDING"),
    seated: translate("SEATED"),
    floor: translate("FLOOR"),
    jumping: translate("JUMPING"),
  };
  return stanceTranslations[stance] || null;
}

export function getGoalTranslation(goal: FitnessGoalType) {
  const goalTranslations: { [goal in FitnessGoalType]: string } = {
    GOAL_HEALTH: translate("MAINTAINING_HEALTH"),
    GOAL_LOSE: translate("LOSING_WEIGHT"),
    GOAL_BUILD: translate("BUILDING_STRENGTH"),
    GOAL_FLEXIBILITY: translate("INCREASING_FLEXIBILITY"),
    GOAL_INJURY: translate("PREVENTING_INJURY"),
    GOAL_ENDURANCE: translate("IMPROVING_ENDURANCE"),
    GOAL_TONE: translate("TONING_MUSCLE"),
    GOAL_SPORTS: translate("IMPROVING_PERFORMANCE"),
    GOAL_RELAXATION: translate("GOAL_RELAXATION"),
  };
  return goalTranslations[goal] || null;
}

export function getSubCategoryTranslation(
  subCategory: WorkoutObject["subCategory"],
) {
  const subCategoryTranslations: {
    [key in WorkoutObject["subCategory"]]: string;
  } = {
    full: translate("FULL_BODY"),
    upper: translate("UPPER_BODY"),
    lower: translate("LOWER_BODY"),
    core: translate("CORE_AND_ABS"),
    back: translate("BACK"),
    butt: translate("GLUTES"),
  };
  return subCategoryTranslations[subCategory] || null;
}

export function getDifficultyTranslation(
  difficulty: WorkoutObject["difficulty"] | ExerciseObject["difficulty"],
) {
  const difficultyTranslations: {
    [level in WorkoutObject["difficulty"]]: string;
  } = {
    1: translate("BEGINNER"),
    2: translate("INTERMEDIATE"),
    3: translate("ADVANCED"),
  };
  return difficultyTranslations[difficulty] || null;
}

export function getCategoryTranslation(
  category: CategorySlug | WorkoutLogType | ExerciseCategory,
) {
  const categoryTranslations:
    | {
        [key in StudioWorkoutObject["category"]]: string;
      }
    | {
        [key in ExerciseObject["category"]]: string;
      }
    | {
        [key in WorkoutObject["category"]]: string;
      }
    | { [key in WorkoutLogType]: string } = {
    // @todo could we / should we consolidate these types?
    cardio: translate("CARDIO"),
    yoga: translate("YOGA"),
    strength: translate("STRENGTH"),
    stretching: translate("STRETCHING"),
    pilates: translate("PILATES"),
    stretch: translate("STRETCHING"),
    pt: translate("PHYSICAL_THERAPY_SHORT"),
    barre: translate("BARRE"),
    meditation: translate("MEDITATION"),
    mobility: translate("MOBILITY"),
    dance: translate("DANCE"),
    boxing: translate("BOXING"),
    "tai-chi": translate("TAI_CHI"),
    pelvic: translate("PELVIC_FLOOR"),
    "external-activity": translate("OTHER"),
    custom: translate("CUSTOM"),
    plan: translate("MY_PLAN"),
    upper: translate("UPPER_BODY"),
    lower: translate("LOWER_BODY"),
    core: translate("CORE_STRENGTH"),
  };
  return categoryTranslations[category] || null;
}

export function getGoalTypeTranslation(goalType: GoalType) {
  const goalTranslations: { [key in GoalType]: string } = {
    leaner: translate("LEANER"),
    fitter: translate("FITTER"),
    stronger: translate("STRONGER"),
    custom: translate("CUSTOM"),
  };
  return goalTranslations[goalType] || null;
}

export function getMeditationSubcategoryTranslation(subCategory) {
  const subCategoryTranslations: { [key in MeditationCategory]: string } = {
    "controlling-stress-and-anxiety": translate(
      "CONTROLLING_STRESS_AND_ANXIETY",
    ),
    "daily-practice": translate("DAILY_PRACTICE"),
    "for-kids": translate("FOR_KIDS"),
    "health-and-happiness": translate("HEALTH_AND_HAPPINESS"),
    sleep: translate("SLEEP"),
    techniques: translate("TECHNIQUES"),
    unguided: translate("UNGUIDED"),
  };
  return subCategoryTranslations[subCategory] || null;
}

export async function sendFirestoreTranslationRequest(
  firestoreDocId: string,
  firestoreCollection: FirestoreDataType,
  firestoreDataKey?: keyof FirestoreObject,
  languages?: LanguageString[],
) {
  console.log("location port", location?.port);
  const url = new URL(
    location?.port == "5555"
      ? "http://localhost:5001/sworkit-user/us-central1/updateTranslations"
      : "https://us-central1-sworkit-user.cloudfunctions.net/updateTranslations",
  );
  url.searchParams.append("docId", firestoreDocId);
  url.searchParams.append("docCollection", firestoreCollection);

  if (firestoreDataKey) {
    url.searchParams.append("docKey", firestoreDataKey as string);
  }

  if (languages && languages.length > 0) {
    url.searchParams.append("languages", languages.join(","));
  }

  try {
    const response = await fetch(url.toString());

    // Log the status code
    console.log(`Response Status: ${response.status}`);

    if (!response.ok) {
      // Try to read error message from the response
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log("Success:", data);
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return error;
  }
}
